import { useState } from "react";
import {
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  addToast,
} from "@heroui/react";
import { ShieldOff } from "lucide-react";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeInputOtp } from "@/Components/NextBase/LimeInputOtp";
import { api } from "@/lib/api-client";
import { isMobileSafari } from "react-device-detect";
import { isNativePlatform } from "@/utils/native";

type MfaResetModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export const MfaResetModal = ({
  isOpen,
  onClose,
  onSuccess,
}: MfaResetModalProps) => {
  const [useRecovery, setUseRecovery] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");

  const { mutateAsync: resetMfa, isPending } = api.mfa.useMfaReset();

  const handleReset = async () => {
    const code = useRecovery ? recoveryCode : totpCode;
    if (!code) return;

    try {
      await resetMfa({
        type: useRecovery ? "recovery" : "totp",
        code,
      });

      addToast({
        title: "MFA disabled",
        description:
          "Two-factor authentication has been removed. You will be logged out.",
        color: "success",
      });

      setTimeout(() => onSuccess(), 1500);
    } catch {
      addToast({
        title: "Error",
        description: useRecovery
          ? "Invalid recovery code. Please try again."
          : "Invalid verification code. Please try again.",
        color: "danger",
      });

      if (useRecovery) {
        setRecoveryCode("");
      } else {
        setTotpCode("");
      }
    }
  };

  const handleClose = () => {
    setTotpCode("");
    setRecoveryCode("");
    setUseRecovery(false);
    onClose();
  };

  return (
    <LimeModal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalContent>
        <ModalHeader className="flex items-center gap-2">
          <ShieldOff className="text-danger" size={20} />
          Reset Two-Factor Authentication
        </ModalHeader>
        <ModalBody>
          <p className="text-default-600">
            {useRecovery
              ? "Enter one of your recovery codes to disable MFA."
              : "Enter the 6-digit code from your authenticator app to disable MFA."}
          </p>

          <div className="mt-2">
            {useRecovery ? (
              <LimeInput
                label="Recovery code"
                data-identifier="mfa-reset-recovery-input"
                size="sm"
                className="w-full"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.target.value)}
                autoFocus={!isMobileSafari && !isNativePlatform()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && recoveryCode.length > 0) {
                    handleReset();
                  }
                }}
              />
            ) : (
              <div className="flex justify-center">
                <LimeInputOtp
                  length={6}
                  variant="bordered"
                  value={totpCode}
                  onChange={(val: string) => setTotpCode(val)}
                  onComplete={handleReset}
                  data-identifier="mfa-reset-totp-input"
                  autoFocus={!isMobileSafari && !isNativePlatform()}
                />
              </div>
            )}
          </div>

          <Button
            variant="light"
            size="sm"
            className="mt-1 self-start"
            onPress={() => setUseRecovery((v) => !v)}
          >
            {useRecovery
              ? "Use authenticator code instead"
              : "Use recovery code instead"}
          </Button>
        </ModalBody>
        <ModalFooter>
          <Button variant="flat" onPress={handleClose}>
            Cancel
          </Button>
          <Button
            color="danger"
            onPress={handleReset}
            isLoading={isPending}
            isDisabled={useRecovery ? !recoveryCode : totpCode.length !== 6}
          >
            Disable MFA
          </Button>
        </ModalFooter>
      </ModalContent>
    </LimeModal>
  );
};
